/**
 * KpiTile — a single console metric (label + value + optional hint/tone). Token-driven so it themes for
 * free across pro/claire/dark. The numeric value uses the mono face + an LTR island so figures read
 * identically in FR and AR. No raw hex (L-2) — every color is a semantic token. Mirrors the admin KpiTile.
 */
import type { ReactNode } from "react";
import { Card } from "@/ui";

export type KpiTone = "neutral" | "accent" | "success" | "warning" | "danger";

const TONE_VAR: Record<KpiTone, string> = {
  neutral: "var(--nv-text-0)",
  accent: "var(--nv-accent, var(--nv-azure-600))",
  success: "var(--nv-success, var(--nv-normal))",
  warning: "var(--nv-warning, var(--nv-high))",
  danger: "var(--nv-danger, var(--nv-critical))",
};

export interface KpiTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: KpiTone;
  icon?: ReactNode;
}

export function KpiTile({ label, value, hint, tone = "neutral", icon }: KpiTileProps) {
  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--nv-s-2)",
            color: "var(--nv-text-2, var(--nv-slate))",
            fontSize: "var(--nv-fs-label)",
          }}
        >
          {icon && <span aria-hidden>{icon}</span>}
          <span>{label}</span>
        </div>
        <div
          className="nv-ltr-island"
          style={{
            fontSize: "var(--nv-fs-display, var(--nv-fs-h1))",
            fontFamily: "var(--nv-font-mono)",
            fontWeight: 600,
            color: TONE_VAR[tone],
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
        {hint && (
          <div style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {hint}
          </div>
        )}
      </div>
    </Card>
  );
}
